import { FC, useMemo } from 'react';
import { MenuItem, Typography, makeStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import { useDeploymentPreviewContext } from './context/useDeploymentPreviewContext';
import {
  useWbMenuButtonContext,
  WbButton,
  WbCardActions,
  WbMenuButton,
} from '@agilelab/plugin-wb-platform';
import { getAllowedActions } from './utils';
import { Action } from '@agilelab/plugin-wb-builder-common';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
    containerType: 'inline-size',
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    [theme.breakpoints.down('xs')]: {
      padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
    },
  },
  actionBox: {
    display: 'flex',
    alignItems: 'center',
    gap: `${theme.spacing(2)}px`,
  },
  selectInput: {
    height: '36px',
    minHeight: 'auto',
    fontSize: theme.typography.body1.fontSize,
  },
  selectTextField: {
    minHeight: 0,
  },

  headerTitleContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '1 1 0',
    overflow: 'hidden',
  },
}));

export const DeploymentSummary: FC = () => {
  const classes = useStyles();
  const { temporarySelectedItems, isAdvanced, setIsAdvanced, onChangeActions } =
    useDeploymentPreviewContext();
  const allowedActionsForSelectedItems = useMemo(() => {
    return Array.from(
      new Set(
        temporarySelectedItems.flatMap(
          item => getAllowedActions(item.status, item.newState!) ?? [],
        ),
      ),
    );
  }, [temporarySelectedItems]);
  return (
    <div className={classes.container}>
      <div className={classes.headerTitleContent}>
        <Typography variant="body1">Deployment summary</Typography>
      </div>

      <WbCardActions breakpoint="w_xs">
        {isAdvanced ? (
          <>
            <WbButton
              size="small"
              variant="outlined"
              label="Basic Provisioning"
              color="primary"
              onClick={() => {
                setIsAdvanced(false);
              }}
              startIcon={<ArrowBackIcon />}
            />
            <WbMenuButton
              label="Bulk action"
              color="primary"
              disabled={!temporarySelectedItems.length}
            >
              <DeploymentSummaryBulkActionMenuItems
                actions={allowedActionsForSelectedItems}
                onChangeActions={onChangeActions}
              />
            </WbMenuButton>
          </>
        ) : (
          <WbButton
            size="small"
            variant="outlined"
            color="primary"
            onClick={() => {
              setIsAdvanced(true);
            }}
            startIcon={<EditIcon />}
            label="Advanced Provisioning"
          />
        )}
      </WbCardActions>
    </div>
  );
};

type DeploymentSummaryBulkActionMenuItemsProps = {
  actions: string[];
  onChangeActions: (action: Action) => void;
};

function DeploymentSummaryBulkActionMenuItems({
  actions,
  onChangeActions,
}: DeploymentSummaryBulkActionMenuItemsProps) {
  const { closeMenu } = useWbMenuButtonContext();

  return (
    <>
      {actions.map(action => (
        <MenuItem
          key={action}
          onClick={() => {
            closeMenu();
            onChangeActions(action as Action);
          }}
        >
          {snakeCaseToTitleCase(action)}
        </MenuItem>
      ))}
    </>
  );
}
